import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Switch, Typography } from '@mui/material'
import { Table } from 'react-bootstrap'
import { UserInfoContext } from '../context/UserInfoProvider'
import dayjs from '../../utils/dayjsConfig'
import {
  getFormattedDate,
  getUserGeofence,
  updateUserGeofence,
  updateUserGeofenceBlock
} from '../services/apiUserService'
import UserGeofenceModal from '../component/modal/UserGeofenceModal'
import UserGeofenceBlockModal from '../component/modal/UserGeofenceBlockModal'
import { canManageUsersApp } from '../../global_context/capabilities/userAppCapabilities'
import LoadingSpinner from '../../components/LoadingSpinner'
import ActionButton from '../../components/action_button/ActionButton'

const HISTORY_PER_PAGE = 20

const BLOCK_SUCCESS_MESSAGE = 'El bloqueo por geocerca se actualizó correctamente.'
const BLOCK_ERROR_MESSAGE =
  'Ocurrió un error al intentar procesar la solicitud. Revisa tu conexión e intenta de nuevo.'
const GENERIC_GEOFENCE_ERROR_MESSAGE =
  'Ocurrió un error inesperado. Intente nuevamente o contacte soporte si el problema persiste.'
const HISTORY_LOAD_ERROR_MESSAGE = 'No fue posible cargar más ajustes registrados.'

const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1'

const isBlockHistoryRow = (row) => {
  if (!row) return false

  return (
    row.setting_key === 'geofence_block' ||
    row.change_type === 'block_toggled' ||
    row.comments === 'Bloqueo por geocerca'
  )
}

const formatBlockValue = (value) => (isTruthyFlag(value) ? 'Activado' : 'Desactivado')

const formatDistance = (value, globalValue) => {
  if (value === null || value === undefined || value === '') {
    return `${globalValue || 100} m (global)`
  }

  return `${value} m`
}

const formatHistoryValue = (row, value, globalValue) => {
  if (isBlockHistoryRow(row)) {
    return formatBlockValue(value)
  }

  return formatDistance(value, globalValue)
}

const changeTypeLabel = (row) => {
  if (isBlockHistoryRow(row)) {
    return 'Bloqueo por geocerca'
  }

  if (row?.comments === 'Geocerca restablecida al valor predeterminado') {
    return row.comments
  }

  const labels = {
    enabled: 'Geocerca personalizada habilitada',
    updated: 'Geocerca editada',
    cleared: 'Geocerca restablecida al valor predeterminado'
  }

  return labels[row?.change_type] || 'Geocerca editada'
}

const getGeofenceComment = ({ isCustomGeofenceEnabled }) => {
  return isCustomGeofenceEnabled
    ? 'Geocerca editada'
    : 'Geocerca personalizada habilitada'
}

const getErrorMessageFromResponse = async (response) => {
  try {
    const data = await response.clone().json()
    return data?.error || data?.message || ''
  } catch (jsonError) {
    try {
      return await response.text()
    } catch (textError) {
      return ''
    }
  }
}

const formatOptionalDateTime = (date) => {
  if (!date) return '-'

  return dayjs(date).isValid() ? dayjs(date).format('DD/MM/YYYY, HH:mm:ss') : getFormattedDate(date)
}

const formatOptionalDate = (date) => {
  if (!date) return '-'

  return dayjs(date).isValid() ? dayjs(date).format('DD/MM/YYYY') : getFormattedDate(date)
}

const historyRowKey = (row, index) =>
  `${row.created_at}-${row.setting_key || row.change_type}-${row.previous_value}-${row.new_value}-${index}`

const UserGeofenceTab = ({ setFailedGeofenceUpdate, setSuccessGeofenceUpdate }) => {
  const { userInfoData } = useContext(UserInfoContext)
  const [isFetching, setIsFetching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showBlockModal, setShowBlockModal] = useState(false)
  const [geofenceData, setGeofenceData] = useState(null)
  const [history, setHistory] = useState([])
  const [historyPage, setHistoryPage] = useState(1)
  const [historyCount, setHistoryCount] = useState(0)
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false)
  const [historyLoadError, setHistoryLoadError] = useState(null)
  const isLoadingMoreHistoryRef = useRef(false)
  const failedGeofenceTimeoutRef = useRef(null)
  const successGeofenceTimeoutRef = useRef(null)

  const canWrite = canManageUsersApp()

  const activeDistance = geofenceData?.active_geo_max_distance_in_meter || 100
  const globalDistance = geofenceData?.business_geo_max_distance_in_meter || 100
  const isCustomGeofenceEnabled = geofenceData?.source === 'user'
  const isBlockEnabled = isTruthyFlag(geofenceData?.geofence_block_enabled)
  const hasMoreHistory = history.length < historyCount

  const lastDistanceHistoryEntry = useMemo(
    () => [...history].reverse().find((row) => !isBlockHistoryRow(row)),
    [history]
  )

  const scheduleFailedGeofenceReset = useCallback(() => {
    if (failedGeofenceTimeoutRef.current) {
      clearTimeout(failedGeofenceTimeoutRef.current)
    }

    failedGeofenceTimeoutRef.current = setTimeout(() => {
      setFailedGeofenceUpdate(false)
      failedGeofenceTimeoutRef.current = null
    }, 10000)
  }, [setFailedGeofenceUpdate])

  const scheduleSuccessGeofenceReset = useCallback(() => {
    if (successGeofenceTimeoutRef.current) {
      clearTimeout(successGeofenceTimeoutRef.current)
    }

    successGeofenceTimeoutRef.current = setTimeout(() => {
      setSuccessGeofenceUpdate(false)
      successGeofenceTimeoutRef.current = null
    }, 10000)
  }, [setSuccessGeofenceUpdate])

  const applyGeofenceResponse = useCallback((data, { append = false } = {}) => {
    setGeofenceData(data)
    setHistoryCount(data?.history_count || data?.history?.length || 0)
    setHistory((previousHistory) =>
      append ? [...previousHistory, ...(data?.history || [])] : data?.history || []
    )
  }, [])

  const fetchUserGeofenceFromServer = useCallback(
    async ({ page = 1, append = false } = {}) => {
      if (!userInfoData?.id) return

      if (append) {
        if (isLoadingMoreHistoryRef.current) return

        isLoadingMoreHistoryRef.current = true
        setIsLoadingMoreHistory(true)
        setHistoryLoadError(null)
      } else {
        setIsFetching(true)
        setHistoryLoadError(null)
      }

      try {
        const response = await getUserGeofence(userInfoData.id, page, HISTORY_PER_PAGE)
        if (!response?.ok) {
          throw new Error(`HTTP error ${response?.status}`)
        }
        const data = await response.json()
        applyGeofenceResponse(data, { append })
        setHistoryPage(page)
      } catch (error) {
        console.error('fetchUserGeofenceFromServer error', error)
        if (append) {
          setHistoryLoadError(HISTORY_LOAD_ERROR_MESSAGE)
        } else {
          setFailedGeofenceUpdate('No fue posible cargar la geocerca del usuario.')
          scheduleFailedGeofenceReset()
        }
      } finally {
        if (append) {
          isLoadingMoreHistoryRef.current = false
          setIsLoadingMoreHistory(false)
        } else {
          setIsFetching(false)
        }
      }
    },
    [
      applyGeofenceResponse,
      scheduleFailedGeofenceReset,
      setFailedGeofenceUpdate,
      userInfoData?.id
    ]
  )

  useEffect(() => {
    fetchUserGeofenceFromServer({ page: 1, append: false })
  }, [fetchUserGeofenceFromServer])

  useEffect(() => {
    return () => {
      if (failedGeofenceTimeoutRef.current) {
        clearTimeout(failedGeofenceTimeoutRef.current)
      }

      if (successGeofenceTimeoutRef.current) {
        clearTimeout(successGeofenceTimeoutRef.current)
      }
    }
  }, [])

  const openGeofenceModal = () => {
    setShowModal(true)
  }

  const openBlockModal = () => {
    if (!canWrite || saving || isFetching) return
    setShowBlockModal(true)
  }

  const closeGeofenceModal = async (confirmed, newDistance) => {
    setShowModal(false)

    if (!confirmed) return

    setSaving(true)

    try {
      const matchesGlobalValue = newDistance === globalDistance
      const response = await updateUserGeofence(
        userInfoData.id,
        newDistance,
        getGeofenceComment({ isCustomGeofenceEnabled })
      )

      if (!response.ok) {
        const errorMessage = await getErrorMessageFromResponse(response)
        throw new Error(errorMessage || `HTTP error ${response.status}`)
      }

      setSuccessGeofenceUpdate(
        matchesGlobalValue
          ? `Geocerca personalizada actualizada para ${userInfoData.uid} - ${userInfoData.full_name}: ${newDistance} m. Actualmente coincide con el valor global del sistema.`
          : `Geocerca actualizada para ${userInfoData.uid} - ${userInfoData.full_name}: ${newDistance} m.`
      )
      scheduleSuccessGeofenceReset()
      await fetchUserGeofenceFromServer({ page: 1, append: false })
    } catch (error) {
      console.error('updateUserGeofence error', error)
      setFailedGeofenceUpdate(GENERIC_GEOFENCE_ERROR_MESSAGE)
      scheduleFailedGeofenceReset()
    } finally {
      setSaving(false)
    }
  }

  const closeBlockModal = async (confirmed) => {
    setShowBlockModal(false)

    if (!confirmed) return

    const nextBlockEnabled = !isBlockEnabled
    setSaving(true)

    try {
      const response = await updateUserGeofenceBlock(userInfoData.id, nextBlockEnabled)

      if (!response.ok) {
        const errorMessage = await getErrorMessageFromResponse(response)
        throw new Error(errorMessage || `HTTP error ${response.status}`)
      }

      setSuccessGeofenceUpdate(BLOCK_SUCCESS_MESSAGE)
      scheduleSuccessGeofenceReset()
      await fetchUserGeofenceFromServer({ page: 1, append: false })
    } catch (error) {
      console.error('updateUserGeofenceBlock error', error)
      setFailedGeofenceUpdate(BLOCK_ERROR_MESSAGE)
      scheduleFailedGeofenceReset()
    } finally {
      setSaving(false)
    }
  }

  const loadNextHistoryPage = () => {
    fetchUserGeofenceFromServer({ page: historyPage + 1, append: true })
  }

  const handleHistoryScroll = (event) => {
    if (
      !hasMoreHistory ||
      historyLoadError ||
      isLoadingMoreHistoryRef.current ||
      isFetching
    ) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight > 80) return

    loadNextHistoryPage()
  }

  const geofenceActionText = 'Modificar geocerca'
  const blockStatusLabel = isBlockEnabled ? 'Activado' : 'Desactivado'
  const blockChipLabel = isBlockEnabled ? 'Bloquea actividades' : 'No bloquea actividades'
  const blockDescription = isBlockEnabled
    ? `El usuario no puede iniciar visitas ni realizar actividades si está fuera de los ${activeDistance} m configurados.`
    : 'El usuario puede iniciar visitas y realizar actividades sin importar su ubicación.'

  return (
    <Box sx={{ pt: 0 }}>
      {showModal && (
        <UserGeofenceModal
          showModal={true}
          closeModal={closeGeofenceModal}
          saving={saving}
          isCustomGeofenceEnabled={isCustomGeofenceEnabled}
          currentDistance={activeDistance}
        />
      )}

      {showBlockModal && (
        <UserGeofenceBlockModal
          showModal={true}
          closeModal={closeBlockModal}
          saving={saving}
          isActivating={!isBlockEnabled}
          activeDistance={activeDistance}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          borderBottom: '1px solid #E5E7EB',
          pb: 2,
          mb: 2
        }}
      >
        <Typography sx={{ fontSize: 18 }}>Configuración de geocerca:</Typography>
        {canWrite && (
          <Box
            className="user-geofence-actions"
            sx={{
              justifyContent: { xs: 'stretch', sm: 'flex-end' }
            }}
          >
            <ActionButton
              text={geofenceActionText}
              icon="instant_mix"
              filled
              color="#FF7A30"
              hoverColor="#F06F28"
              disabled={saving || isFetching}
              onClick={openGeofenceModal}
            />
          </Box>
        )}
      </Box>

      {!isFetching && (
        <>
          <div className="user-geofence-cards">
            <div
              className={`user-geofence-card ${isCustomGeofenceEnabled ? 'user-geofence-card--custom' : ''}`}
            >
              <span className="user-geofence-label">Diámetro activo</span>
              <div className="user-geofence-card__value-row">
                <strong>{activeDistance} m</strong>
                <span
                  className={`user-geofence-badge ${isCustomGeofenceEnabled ? 'user-geofence-badge--custom' : ''}`}
                >
                  {isCustomGeofenceEnabled ? 'Geocerca personalizada' : 'Valor global del sistema'}
                </span>
              </div>
              <p>
                {isCustomGeofenceEnabled
                  ? `Este usuario tiene una geocerca personalizada desde ${formatOptionalDate(lastDistanceHistoryEntry?.created_at)}.`
                  : 'Este usuario no tiene una geocerca personalizada. Se aplica el valor predeterminado del sistema.'}
              </p>
            </div>

            <div
              className={`user-geofence-card user-geofence-card--block ${isBlockEnabled ? 'user-geofence-card--block-enabled' : ''}`}
            >
              <div className="user-geofence-card__header">
                <div>
                  <span className="user-geofence-label">Bloqueo por geocerca</span>
                  <div className="user-geofence-card__value-row">
                    <strong className="user-geofence-card__status">{blockStatusLabel}</strong>
                    <span
                      className={`user-geofence-badge ${isBlockEnabled ? 'user-geofence-badge--block-enabled' : ''}`}
                    >
                      {blockChipLabel}
                    </span>
                  </div>
                </div>
                <Switch
                  checked={isBlockEnabled}
                  disabled={!canWrite || saving || isFetching}
                  onChange={openBlockModal}
                  inputProps={{ 'aria-label': 'Bloqueo por geocerca' }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#FF7A30'
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#FF7A30'
                    }
                  }}
                />
              </div>
              <p>{blockDescription}</p>
            </div>
          </div>

          <div className="mt-25">
            <b>Ajustes registrados</b>
          </div>
          <div className="mt-20 user-geofence-history" onScroll={handleHistoryScroll}>
            <Table hover responsive>
              <thead className="table-header-gray-mui">
                <tr>
                  <th className="vertical-align-middle" style={{ padding: '10px 25px' }}>
                    <span className="th-header-text">FECHA Y HORA</span>
                  </th>
                  <th className="vertical-align-middle">
                    <span className="th-header-text">TIPO DE CAMBIO</span>
                  </th>
                  <th className="vertical-align-middle">
                    <span className="th-header-text">REGISTRO ANTERIOR</span>
                  </th>
                  <th className="vertical-align-middle">
                    <span className="th-header-text">REGISTRO NUEVO</span>
                  </th>
                  <th className="vertical-align-middle">
                    <span className="th-header-text">ACTUALIZADO POR</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map((row, index) => (
                  <tr key={historyRowKey(row, index)}>
                    <td className="td-text-limited td-text-limited-space">
                      {formatOptionalDateTime(row.created_at)}
                    </td>
                    <td className="td-text-limited">{changeTypeLabel(row)}</td>
                    <td className="td-text-limited">
                      {formatHistoryValue(row, row.previous_value, globalDistance)}
                    </td>
                    <td className="td-text-limited">
                      {formatHistoryValue(row, row.new_value, globalDistance)}
                    </td>
                    <td className="td-text-limited">{row.changed_by_email}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr>
                    <td colSpan="5" className="td-text-no-border">
                      Este usuario no tiene ajustes registrados.
                    </td>
                  </tr>
                )}
                {isLoadingMoreHistory && (
                  <tr>
                    <td colSpan="5" className="td-text-no-border">
                      Cargando más registros...
                    </td>
                  </tr>
                )}
                {historyLoadError && !isLoadingMoreHistory && (
                  <tr>
                    <td colSpan="5" className="td-text-no-border">
                      <div className="user-geofence-history-error">
                        <span>{historyLoadError}</span>
                        <button
                          type="button"
                          className="user-geofence-history-retry"
                          onClick={loadNextHistoryPage}
                        >
                          Reintentar
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      {(isFetching || saving) && (
        <div className="updating-screen">
          <div className="loading-card">
            <LoadingSpinner text="Actualizando" size={25} color="#747474" />
          </div>
        </div>
      )}
    </Box>
  )
}

export default UserGeofenceTab
